import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BookOpen, Compass } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import BrandSignoff from "@/components/BrandSignoff";
import Seo from "@/components/Seo";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Objective independence",
    body: "No developer kickbacks, no council bias. Our only interest is that you understand your position before you commit.",
  },
  {
    icon: BookOpen,
    title: "Clarity over jargon",
    body: "Four-hundred-page planning instruments, translated into plain English and practical decision trees.",
  },
  {
    icon: Compass,
    title: "Prevention first",
    body: "Most planning disasters are avoidable at due diligence. We exist to catch them while they're still cheap.",
  },
];

const About = () => (
  <div data-testid="about-page">
    <Seo
      title="About Us"
      description="Why PLAN FIRST NSW exists — an independent planning education and advisory service helping NSW property owners understand before they commit."
    />
    <section className="bg-sand-light py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Eyebrow>About PLAN FIRST</Eyebrow>
        <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-3xl text-balance text-teal">
          Bridging the gap between complex planning law and everyday Australians
        </h1>
      </div>
    </section>

    <section className="py-16 lg:py-24 bg-white border-b border-cloud" data-testid="about-purpose">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>Why We Exist</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-teal text-balance">
            Understand the process. Ask better questions. Plan first.
          </h2>
          <div className="mt-6 space-y-4 text-charcoal/75 leading-relaxed">
            <p>
              Navigating the NSW planning system shouldn't mean starting from scratch or feeling
              overwhelmed by unfamiliar rules and processes.
            </p>
            <p>
              Plan First NSW brings together accessible planning education, practical toolkits,
              interactive workbooks and guided information resources to help you understand the
              development journey. Whether you're researching a property, considering renovations,
              exploring opportunities for your land or working through the next stages of an
              approved development, our resources help you identify the information you need and
              prepare the questions worth asking.
            </p>
            <p>
              We're here to help you understand the process before you take the next step. Our
              purpose is education, awareness and preparation — we don't replace council,
              registered certifiers, planning consultants, lawyers or other qualified advisers.
            </p>
            <p className="font-medium text-teal">
              Information first. Professional advice when you need it.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>

    <section className="py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <FadeIn className="lg:col-span-5">
          <div className="bg-teal text-ivory p-10 lg:p-12 max-w-md" data-testid="founder-card">
            <img
              src="/assets/logo_circle.jpeg"
              alt="PLAN FIRST — Understand before you commit."
              className="w-24 h-24 rounded-full object-cover border-2 border-sand/50"
            />
            <p className="mt-8 font-serif text-xl sm:text-2xl leading-relaxed">
              "Understanding your property's potential should come before committing your
              money, time or dreams."
            </p>
            <p className="mt-6 text-[11px] font-mono uppercase tracking-[0.18em] text-sand">
              The founding principle of PLAN FIRST.
            </p>
          </div>
          <div className="mt-6 max-w-md">
            <p className="font-serif text-xl text-teal">Our Founder</p>
            <p className="mt-2 text-xs font-mono uppercase tracking-[0.14em] text-charcoal/55 leading-relaxed">
              20+ years across customer service, property development, planning &amp; government ·
              Private consultancy · Local government · NSW education infrastructure
            </p>
          </div>
        </FadeIn>

        <div className="lg:col-span-7 space-y-6">
          <FadeIn>
            <p className="font-serif text-xl sm:text-2xl text-teal leading-relaxed">
              "I believe understanding your property's potential should come before committing
              your money, time or dreams."
            </p>
          </FadeIn>
          {[
            "Welcome to PLAN FIRST. — Understand before you commit. I'm the founder of PLAN FIRST., a NSW-based property planning and development education brand created to make planning information more accessible, understandable and practical.",
            "My professional background spans more than 20 years across customer service, property development, planning and government. I've worked in private planning consultancy, local government and statutory planning for NSW education infrastructure, developing an understanding of the opportunities, complexities and challenges involved in property and development.",
            "Throughout my career, one thing has remained constant: my passion for helping people navigate complex processes, solve problems and achieve the best possible outcomes.",
            "Whether you're buying your first home, purchasing vacant land, considering renovations, exploring subdivision opportunities or investing in property, planning decisions can significantly influence what's possible. Zoning, environmental constraints, development controls, infrastructure requirements and approval pathways can be confusing, particularly when you're unfamiliar with the planning system.",
            "That's why I created PLAN FIRST. My goal is to take the complexity out of property planning and provide clear, practical educational resources that help you identify potential issues, understand the right questions to ask and approach your next property decision with greater confidence.",
          ].map((p, i) => (
            <FadeIn key={i} delay={i * 0.05}>
              <p className="text-base sm:text-lg text-charcoal/85 leading-relaxed">{p}</p>
            </FadeIn>
          ))}
          <FadeIn delay={0.15}>
            <p className="font-serif text-lg sm:text-xl text-teal leading-relaxed">
              Because informed decisions create better outcomes.
              <span className="block mt-1 text-sm font-mono uppercase tracking-[0.18em] text-charcoal/55">
                PLAN FIRST. — Understand before you commit.
              </span>
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <Link
              to="/book"
              data-testid="about-cta-book"
              className="inline-flex items-center gap-2 bg-teal text-ivory font-medium px-7 py-3.5 rounded-full hover:bg-teal-dark transition-colors mt-2"
            >
              Planner Connect — coming soon <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-6 text-xs text-charcoal/55 leading-relaxed max-w-xl">
              Our resources provide general educational information and do not replace
              site-specific professional planning, legal or financial advice. PLAN FIRST. is
              independent and is not affiliated with or endorsed by any government authority.
            </p>
          </FadeIn>
        </div>
      </div>
    </section>

    <section className="py-16 lg:py-24 bg-sand-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>What We Stand For</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-teal">
            Three commitments behind everything we publish
          </h2>
        </FadeIn>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {VALUES.map((v, i) => (
            <FadeIn key={v.title} delay={i * 0.08} className="h-full">
              <div className="h-full bg-white border border-cloud p-8 hover:border-teal hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <v.icon className="w-7 h-7 text-teal" />
                <h3 className="mt-5 font-serif text-xl text-teal">{v.title}</h3>
                <p className="mt-3 text-sm text-charcoal/70 leading-relaxed">{v.body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <BrandSignoff />
  </div>
);

export default About;
